// src/components/outputs/PiecesResultEntry.jsx — the ONE body of a history card.
//
// Every format draws the same screen, read from the entry's piece list
// (src/publish/pieces/index.js :: piecesForEntry), top to bottom:
//   1. Qualità          QualityBar — the QA score (metadata.qa_score, else
//                       metadata.style_compliance; null draws nothing)
//   2. Pronto da pubblicare  ReadyToPublish — the primary piece with the card's
//                       ONE «Copia tutto» button (id "all-<entryId>"): it copies
//                       EVERY piece in one go (allPiecesText / copyAllHtml of
//                       publish/downloads.js — the same texts the rows' «Copia»
//                       buttons copy one at a time, meta title, meta description,
//                       slug, hashtags… included; a social format gets text/plain
//                       only, so LinkedIn keeps the line breaks); its counter
//                       counts the primary piece's paste-ready text (primaryText)
//                       against the limit
//   3. the other pieces  one PieceRow each, in list order (label, destination,
//                       value, counter of pieceText, «Copia»); operational extras
//                       (cta_script, thumbnail, slides with their visual cue)
//                       are pieces like any other and need no special case
//   4. MetaFooter        word count / reading time, only when content carries
//                       word_count / reading_time
// The blog's «Fonti citate» ('sources', from content.references) is a row like
// any other: the screen lists the references once, there. The claims and the
// consulted sources of a run are not drawn here but by the card
// (components/claims/ ClaimsPanel, SourcesPanel, from results.verification).
// Nothing here reads `content` to draw a value: the builders did. A 'source'
// piece (the blog's body_html) folds into a <details>, a 'tags' piece prints
// chips — both inside PieceRow / PieceValue. No markup is ever injected.
// The piece list is memoised on the entry AND the UI language: labels and
// destinations are resolved through i18n when the list is built, so a language
// switch rebuilds it (piecesForEntry caches per entry and language).
//
//   PiecesResultEntry({ entry, copiedId, onCopy, t, w })
//   t = the theme of the surface (wizard dark, console LIGHT_T), w(alpha) its
//   rgba helper; the accent is entry.modeConfig.color.

import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { COLORS } from '../../constants/theme.js';
import { piecesForEntry, primaryPiece } from '../../publish/pieces/index.js';
import { primaryText, allPiecesText, copyAllHtml } from '../../publish/downloads.js';
import { MetaFooter } from './OutputPrimitives.jsx';
import QualityBar from './pieces/QualityBar.jsx';
import ReadyToPublish from './pieces/ReadyToPublish.jsx';
import PieceRow from './pieces/PieceRow.jsx';

const defaultW = (o) => `rgba(255,255,255,${o})`;

const positiveInt = (v) => (typeof v === 'number' && Number.isFinite(v) && v > 0 ? v : null);
const nonEmptyString = (v) => (typeof v === 'string' && v.trim() ? v.trim() : '');

const PiecesResultEntry = ({ entry, copiedId, onCopy, t = {}, w = defaultW }) => {
  const { t: tr, i18n } = useTranslation('outputs');
  const language = i18n.language;
  const r = entry?.results || {};
  const content = r.content && typeof r.content === 'object' ? r.content : {};
  const entryId = entry?.id ?? 'entry';
  const color = entry?.modeConfig?.color || COLORS.accent;

  // `language` is a dependency on purpose: the labels inside the list follow it.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const pieces = useMemo(() => piecesForEntry(entry), [entry, language]);
  const primary = useMemo(() => primaryPiece(pieces), [pieces]);
  const readyText = useMemo(() => primaryText(pieces), [pieces]);
  const allText = useMemo(() => allPiecesText(pieces), [pieces]);
  const allHtml = useMemo(() => copyAllHtml(entry, pieces), [entry, pieces]);
  const others = useMemo(() => pieces.filter((p) => p !== primary), [pieces, primary]);

  if (!primary) return null;

  const qaScore = r.metadata?.qa_score ?? r.metadata?.style_compliance ?? null;
  const wordCount = positiveInt(content.word_count);
  const readingTime = nonEmptyString(content.reading_time);

  return (
    <div data-testid="pieces-result-entry">
      <QualityBar score={qaScore} t={t} />

      <ReadyToPublish
        piece={primary}
        entryId={entryId}
        copiedId={copiedId}
        onCopy={onCopy}
        t={t}
        w={w}
        color={color}
        text={readyText}
        copyText={allText}
        copyHtml={allHtml}
      />

      {others.map((piece) => (
        <PieceRow
          key={piece.id}
          piece={piece}
          entryId={entryId}
          copiedId={copiedId}
          onCopy={onCopy}
          t={t}
          w={w}
          color={color}
        />
      ))}

      <MetaFooter
        items={[
          wordCount != null ? tr('common.wordCount', { count: wordCount }) : null,
          readingTime ? tr('pieces.ui.readingTime', { time: readingTime }) : null,
        ]}
      />
    </div>
  );
};

export default PiecesResultEntry;
